import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { orderService } from '../services/api';
import Navbar from '../components/Navbar';
import OrderDetail from '../components/OrderDetail';

const ESTADOS = ['Pendiente','En Taller','Lista','Entregada'];
const COLORS = { 'Pendiente':'#f59e0b','En Taller':'#3b82f6','Lista':'#8b5cf6','Entregada':'#10b981','Anulada':'#9ca3af' };
const fmt = n => '₡' + Number(n).toLocaleString('es-CR');

export default function Dashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('Todos');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mostrarAnuladas, setMostrarAnuladas] = useState(false);

  const load = useCallback(async (s) => {
    try {
      const res = await orderService.getAll(s);
      setOrders(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  useEffect(() => {
    const interval = setInterval(() => { load(search); }, 20000);
    return () => clearInterval(interval);
  }, [load, search]);

  useEffect(() => {
    const timer = setTimeout(() => load(search), 400);
    return () => clearTimeout(timer);
  }, [search, load]);

  const updateStatus = async (id, status) => {
    try {
      await orderService.updateStatus(id, status);
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
      if (selected?.id === id) setSelected(prev => ({ ...prev, status }));
    } catch (err) {
      alert('Error al actualizar estado');
    }
  };

  const handleOrderSaved = async (updatedOrder) => {
    try {
      const res = await orderService.getOne(updatedOrder.id);
      setOrders(prev => prev.map(o => o.id === updatedOrder.id ? res.data : o));
      setSelected(res.data);
    } catch {
      await load(search);
      setSelected(null);
    }
  };

  const activas = orders.filter(o => o.status !== 'Anulada');
  const entregadas = activas.filter(o => o.status === 'Entregada');
  const anuladas = orders.filter(o => o.status === 'Anulada');

  const stats = {
    total: activas.length,
    pendientes: activas.filter(o => o.status === 'Pendiente').length,
    taller: activas.filter(o => o.status === 'En Taller').length,
    entregadas: entregadas.length,
    cobrado: entregadas.reduce((s,o) => s + Number(o.amount) + Number(o.shipping), 0),
  };

  const listas = activas.filter(o => o.status === 'Lista');

  const planearRuta = () => {
    const destinos = listas.map(o => encodeURIComponent(o.cliente_direccion || o.direccion || '')).filter(Boolean);
    if (destinos.length === 0) return alert('No hay direcciones disponibles');
    const url = destinos.length === 1
      ? `https://www.google.com/maps/dir/?api=1&destination=${destinos[0]}`
      : `https://www.google.com/maps/dir/${destinos.join('/')}`;
    window.open(url, '_blank');
  };

  const vendedores = {};
  activas.forEach(o => {
    if (!vendedores[o.vendedor]) vendedores[o.vendedor] = { entregadas:0, enProceso:0, total:0 };
    if (o.status === 'Entregada') { vendedores[o.vendedor].entregadas++; vendedores[o.vendedor].total += Number(o.amount) + Number(o.shipping); }
    else vendedores[o.vendedor].enProceso++;
  });

  const s = {
    main: { maxWidth:1100, margin:'0 auto', padding:'20px 14px' },
    card: { background:'#fff', borderRadius:10, boxShadow:'0 1px 6px rgba(0,0,0,0.09)', padding:22, marginBottom:18 },
    statGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:12, marginBottom:18 },
    stat: c => ({ background:c, borderRadius:10, padding:'14px 16px', color:'#fff', textAlign:'center' }),
    h2: { margin:'0 0 16px', color:'#5c3d2e', fontSize:18 },
    badge: est => ({ background:COLORS[est]||'#aaa', color:'#fff', padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, display:'inline-block' }),
    table: { width:'100%', borderCollapse:'collapse', fontSize:13 },
    th: { background:'#f5ede6', padding:'9px 11px', textAlign:'left', fontWeight:600, color:'#5c3d2e', borderBottom:'2px solid #e0d0c0' },
    td: { padding:'9px 11px', borderBottom:'1px solid #f0e8e0', verticalAlign:'middle' },
    btn: (a) => ({ background:a?'#5c3d2e':'#f0ebe5', color:a?'#fff':'#5c3d2e', border:'none', padding:'6px 14px', borderRadius:7, cursor:'pointer', fontSize:12, marginRight:4 }),
    select: { padding:'6px 10px', border:'1px solid #ddd', borderRadius:7, fontSize:12, cursor:'pointer' },
    searchBox: { width:'100%', padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', marginBottom:14, outline:'none' },
  };

  const searchLower = search.toLowerCase();
  const matchSearch = o => !search || [o.numero, o.cliente_nombre, o.article].some(v => v?.toLowerCase().includes(searchLower));
  const activasFiltradas = (filter === 'Todos' ? activas : activas.filter(o => o.status === filter)).filter(matchSearch);
  const anuladasFiltradas = mostrarAnuladas ? anuladas.filter(matchSearch) : [];
  const listaFinal = [...activasFiltradas, ...anuladasFiltradas];

  return (
    <div style={{ fontFamily:"'Segoe UI',sans-serif", minHeight:'100vh', background:'#f8f5f0' }}>
      <Navbar />
      <div style={s.main}>
        {loading ? <div style={{ textAlign:'center', padding:40, color:'#aaa' }}>Cargando...</div> : (
          <>
            <div style={s.statGrid}>
              <div style={s.stat('#5c3d2e')}><div style={{ fontSize:26, fontWeight:700 }}>{stats.total}</div><div style={{ fontSize:12, opacity:0.9 }}>Órdenes Activas</div></div>
              <div style={s.stat('#f59e0b')}><div style={{ fontSize:26, fontWeight:700 }}>{stats.pendientes}</div><div style={{ fontSize:12, opacity:0.9 }}>Pendientes</div></div>
              <div style={s.stat('#3b82f6')}><div style={{ fontSize:26, fontWeight:700 }}>{stats.taller}</div><div style={{ fontSize:12, opacity:0.9 }}>En Taller</div></div>
              <div style={s.stat('#10b981')}><div style={{ fontSize:26, fontWeight:700 }}>{stats.entregadas}</div><div style={{ fontSize:12, opacity:0.9 }}>Entregadas</div></div>
              {user.role === 'admin' && <div style={s.stat('#a0522d')}><div style={{ fontSize:15, fontWeight:700 }}>{fmt(stats.cobrado)}</div><div style={{ fontSize:12, opacity:0.9 }}>Ventas Cobradas</div></div>}
            </div>

            <div style={s.card}>
              <h2 style={s.h2}>{user.role === 'admin' ? 'Todas las Órdenes' : 'Mis Órdenes'}</h2>
              <input style={s.searchBox} placeholder="🔍 Buscar por número de orden, cliente o artículo..." value={search} onChange={e=>setSearch(e.target.value)}/>
              <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
                {['Todos',...ESTADOS].map(e => (
                  <button key={e} onClick={() => setFilter(e)} style={s.btn(filter===e)}>{e}</button>
                ))}
                <button
                  onClick={() => setMostrarAnuladas(p => !p)}
                  style={{ background: mostrarAnuladas ? '#9ca3af' : '#f0ebe5', color: mostrarAnuladas ? '#fff' : '#9ca3af', border:'1px solid #d1d5db', padding:'6px 14px', borderRadius:7, cursor:'pointer', fontSize:12, marginLeft:'auto' }}
                >
                  {mostrarAnuladas ? '🙈 Ocultar Anuladas' : `👁 Mostrar Anuladas${anuladas.length > 0 ? ` (${anuladas.length})` : ''}`}
                </button>
              </div>

              {listaFinal.length === 0
                ? <div style={{ color:'#aaa', textAlign:'center', padding:28 }}>No se encontraron órdenes.</div>
                : <div style={{ overflowX:'auto' }}>
                  <table style={s.table}>
                    <thead><tr>
                      <th style={s.th}>N° Orden</th><th style={s.th}>Cliente</th><th style={s.th}>Artículo</th>
                      <th style={s.th}>F. Entrega</th>{user.role==='admin'&&<th style={s.th}>Vendedor</th>}
                      <th style={s.th}>Total</th><th style={s.th}>Estado</th><th style={s.th}>Acciones</th>
                    </tr></thead>
                    <tbody>
                      {listaFinal.map(o => {
                        const anulada = o.status === 'Anulada';
                        return (
                          <tr key={o.id} style={{ cursor:'pointer', opacity: anulada ? 0.6 : 1, background: anulada ? '#f9fafb' : 'transparent' }} onClick={() => setSelected(o)}>
                            <td style={s.td}><strong style={{ color: anulada ? '#9ca3af' : '#a0522d' }}>{o.numero}</strong></td>
                            <td style={s.td}><strong style={{ color: anulada ? '#9ca3af' : 'inherit' }}>{o.cliente_nombre||o.nombre}</strong></td>
                            <td style={s.td}><span style={{ color: anulada ? '#9ca3af' : 'inherit' }}>{o.article}</span></td>
                            <td style={s.td}><span style={{ color: anulada ? '#9ca3af' : 'inherit' }}>{o.delivery_date?.split('T')[0]}</span></td>
                            {user.role==='admin'&&<td style={s.td}><span style={{ color: anulada ? '#9ca3af' : 'inherit' }}>{o.vendedor}</span></td>}
                            <td style={s.td}><span style={{ color: anulada ? '#9ca3af' : 'inherit' }}>{fmt(Number(o.amount)+Number(o.shipping))}</span></td>
                            <td style={s.td}><span style={s.badge(o.status)}>{o.status}</span></td>
                            <td style={s.td} onClick={e=>e.stopPropagation()}>
                              {!anulada && user.role==='admin'
                                ? <select style={s.select} value={o.status} onChange={e=>updateStatus(o.id,e.target.value)}>
                                    {ESTADOS.map(est=><option key={est}>{est}</option>)}
                                  </select>
                                : <button style={s.btn(false)} onClick={()=>setSelected(o)}>Ver</button>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              }
            </div>

            {user.role === 'admin' && Object.keys(vendedores).length > 0 && (
              <div style={s.card}>
                <h2 style={s.h2}>📊 Ventas por Vendedor <span style={{ fontSize:12, color:'#aaa', fontWeight:400 }}>(solo Entregadas)</span></h2>
                <div style={{ overflowX:'auto' }}>
                  <table style={s.table}>
                    <thead><tr>
                      <th style={s.th}>Vendedor</th><th style={s.th}>Entregadas</th><th style={s.th}>En Proceso</th><th style={s.th}>Total Cobrado</th>
                    </tr></thead>
                    <tbody>
                      {Object.entries(vendedores).map(([nombre, v]) => (
                        <tr key={nombre}>
                          <td style={s.td}><strong>{nombre}</strong></td>
                          <td style={s.td}><span style={s.badge('Entregada')}>{v.entregadas}</span></td>
                          <td style={s.td}>{v.enProceso > 0 ? <span style={{ color:'#f59e0b', fontWeight:600 }}>{v.enProceso}</span> : <span style={{ color:'#aaa' }}>—</span>}</td>
                          <td style={s.td}><strong style={{ color:'#10b981' }}>{fmt(v.total)}</strong></td>
                        </tr>
                      ))}
                      <tr style={{ background:'#f5ede6' }}>
                        <td style={{ ...s.td, fontWeight:700 }}>TOTAL</td>
                        <td style={{ ...s.td, fontWeight:700 }}>{stats.entregadas}</td>
                        <td style={{ ...s.td, fontWeight:700 }}>{activas.filter(o=>o.status!=='Entregada').length}</td>
                        <td style={{ ...s.td, fontWeight:700, color:'#10b981' }}>{fmt(stats.cobrado)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}


            {user.role === 'admin' && listas.length > 0 && (
              <div style={{ ...s.card, borderLeft: '4px solid #8b5cf6', borderRadius: '0 10px 10px 0' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                  <h2 style={{ ...s.h2, margin:0 }}>
                    🚚 Entregas del día
                    <span style={{ background:'#8b5cf6', color:'#fff', borderRadius:20, padding:'2px 10px', fontSize:13, marginLeft:8 }}>{listas.length}</span>
                  </h2>
                  <button onClick={planearRuta} style={{ background:'#7c3aed', color:'#fff', border:'none', padding:'8px 18px', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13 }}>
                    📍 Planear ruta en Google Maps
                  </button>
                </div>
                <div style={{ overflowX:'auto' }}>
                  <table style={s.table}>
                    <thead><tr>
                      <th style={s.th}>N° Orden</th>
                      <th style={s.th}>Cliente</th>
                      <th style={s.th}>Artículo</th>
                      <th style={s.th}>Dirección</th>
                    </tr></thead>
                    <tbody>
                      {listas.map(o => (
                        <tr key={o.id}>
                          <td style={s.td}><strong style={{ color:'#a0522d' }}>{o.numero}</strong></td>
                          <td style={s.td}>{o.cliente_nombre || o.nombre}</td>
                          <td style={s.td}>{o.article}</td>
                          <td style={s.td}>{o.cliente_direccion || o.direccion || <span style={{ color:'#aaa' }}>Sin dirección</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            )}
          </>
        )}
      </div>
      {selected && (
        <OrderDetail
          order={selected}
          onClose={() => setSelected(null)}
          onStatusChange={updateStatus}
          onOrderSaved={handleOrderSaved}
        />
      )}
    </div>
  );
}
